import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ThemeProvider, StyledEngineProvider, Box, Card, Stack, Typography, TextField, Button, CircularProgress,
} from '@mui/material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import { dataFetcher, endpoints } from 'Api';
import { getDeviceId, registerDevice, startDeviceHeartbeat } from 'Utils/deviceSession';
import { buildTheme } from 'Pages/private/v2/_shared/theme';
import LightRays from 'Components/reactbits/LightRays';
import projectorLogo from 'Assets/Login Logo.png';

// Standalone, chrome-less device-linking page served at /link-device.
//   - No app header / sidebar / footer, no routing side effects.
//   - Not logged in -> inline email + password (no redirect to the login page,
//     no sign-up). A non-existent account just shows the backend error.
//   - Logged in -> enter a device code and link it; success screen offers to
//     link another. Footer shows "Logged in as {email}" + Logout.
//   - /link-device?code=XXXXXX prefills and auto-links once signed in.
//
// Rendered in the V2 design system (dark) so it matches the new UI exactly — the
// Projector wordmark is white, so it needs the dark V2 surface to read.
const LinkDeviceInner = () => {
  const [searchParams] = useSearchParams();
  const urlCode = (searchParams.get('code') || '').replace(/\s/g, '').toUpperCase();

  // 'booting' while we resolve any existing session, then 'auth' | 'link'.
  const [phase, setPhase] = useState('booting');
  const [email, setEmail] = useState('');

  // --- login form state ---
  const [loginEmail, setLoginEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loggingIn, setLoggingIn] = useState(false);
  const [loginError, setLoginError] = useState('');

  // --- link form state ---
  const [code, setCode] = useState(urlCode);
  const [linking, setLinking] = useState(false);
  const [linkError, setLinkError] = useState('');
  const [linked, setLinked] = useState(false);
  const autoLinkedRef = useRef(false);

  // Resolve the signed-in account (if any) from the stored token. We only need
  // the email for the "Logged in as" line; a failure means "not signed in".
  const resolveSession = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) { setPhase('auth'); return; }
    try {
      const res = await dataFetcher(endpoints.getMyProfile);
      if (res?.success && res?.data?.email) {
        setEmail(res.data.email);
        setPhase('link');
      } else {
        setPhase('auth');
      }
    } catch (e) {
      setPhase('auth');
    }
  }, []);

  useEffect(() => { resolveSession(); }, [resolveSession]);

  const handleLogin = async (e) => {
    if (e) e.preventDefault();
    const em = loginEmail.trim();
    if (!em || !password) {
      setLoginError('Enter your email and password.');
      return;
    }
    setLoggingIn(true);
    setLoginError('');
    try {
      const res = await dataFetcher(endpoints.signin, {
        email: em,
        password,
        device_id: getDeviceId(),
        device_type: 'WEB',
      });
      if (res?.success && res?.data?.token) {
        localStorage.setItem('token', res.data.token);
        if (res?.api_response?.access) {
          localStorage.setItem('uploader_token', res.api_response.access);
        }
        if (res?.api_response?.uuid) {
          localStorage.setItem('user_uuid', res.api_response.uuid);
        }
        try { registerDevice(); startDeviceHeartbeat(); } catch (err) { /* noop */ }
        setEmail(res?.data?.email || em);
        setPassword('');
        setPhase('link');
      } else {
        setLoginError(res?.message || 'That account doesn’t exist or the password is wrong.');
      }
    } catch (err) {
      setLoginError(err?.response?.data?.message || 'Could not sign in. Please try again.');
    } finally {
      setLoggingIn(false);
    }
  };

  const handleLink = async (e) => {
    if (e) e.preventDefault();
    const c = code.trim();
    if (c.length < 4) {
      setLinkError('Enter the code shown on your device.');
      return;
    }
    setLinking(true);
    setLinkError('');
    try {
      const res = await dataFetcher(endpoints.registerTv, { code: c });
      if (res?.success) {
        setLinked(true);
        setCode('');
      } else {
        setLinkError(res?.message || 'That code didn’t work. Try again.');
      }
    } catch (err) {
      setLinkError(err?.response?.data?.message || 'Something went wrong linking the device.');
    } finally {
      setLinking(false);
    }
  };

  // Deep-linked code: once on the link screen with a prefilled URL code, submit
  // it automatically (once). If not signed in yet, this waits until after login.
  useEffect(() => {
    if (phase !== 'link' || linked || autoLinkedRef.current) return;
    if (!urlCode || urlCode.length < 4) return;
    autoLinkedRef.current = true;
    handleLink();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, urlCode, linked]);

  const linkAnother = () => {
    setLinked(false);
    setCode('');
    setLinkError('');
    autoLinkedRef.current = true;
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('uploader_token');
    localStorage.removeItem('uploader_token_expiry');
    localStorage.removeItem('user_uuid');
    setEmail('');
    setCode('');
    setLinked(false);
    setLinkError('');
    setLoginEmail('');
    setPassword('');
    setLoginError('');
    setPhase('auth');
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2.25,
        p: { xs: 2, sm: 3 },
        overflowY: 'auto',
        bgcolor: 'background.default',
        color: 'text.primary',
        fontFamily: 'Montserrat, sans-serif',
      }}
    >
      {/* Backdrop: near-black with volumetric light rays fanning down from the
          top — same treatment as the new-UI sign-in screen. */}
      <Box
        aria-hidden
        sx={{
          position: 'fixed',
          inset: 0,
          zIndex: 0,
          overflow: 'hidden',
          pointerEvents: 'none',
          background:
            'radial-gradient(80% 45% at 50% -8%, rgba(255,255,255,0.05), transparent 70%), #060608',
        }}
      >
        <LightRays
          raysOrigin="top-center"
          raysColor="#ffffff"
          raysSpeed={0.9}
          lightSpread={0.85}
          rayLength={1.25}
          fadeDistance={1.1}
          saturation={1}
          followMouse
          mouseInfluence={0.08}
          noiseAmount={0.08}
          distortion={0.04}
        />
      </Box>

      {/* White wordmark logo — decorative, not a link (no home nav). */}
      <Box
        component="img"
        src={projectorLogo}
        alt="Projector"
        draggable={false}
        sx={{
          height: { xs: 42, sm: 50 }, width: 'auto', objectFit: 'contain', userSelect: 'none', position: 'relative', zIndex: 1,
        }}
      />

      <Card
        sx={{
          width: '100%',
          maxWidth: 420,
          p: { xs: 3, sm: 3.75 },
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          zIndex: 1,
        }}
      >
        {phase === 'booting' && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {phase === 'auth' && (
          <Stack component="form" spacing={2} onSubmit={handleLogin}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, textAlign: 'center' }}>
                Sign in to link a device
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', mt: 0.5 }}>
                Use your Projector account to continue.
              </Typography>
            </Box>

            <TextField
              type="email"
              label="Email"
              autoComplete="email"
              fullWidth
              value={loginEmail}
              onChange={(ev) => { setLoginEmail(ev.target.value); if (loginError) setLoginError(''); }}
              placeholder="you@example.com"
              disabled={loggingIn}
            />
            <TextField
              type="password"
              label="Password"
              autoComplete="current-password"
              fullWidth
              value={password}
              onChange={(ev) => { setPassword(ev.target.value); if (loginError) setLoginError(''); }}
              disabled={loggingIn}
            />

            {loginError ? (
              <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 600, textAlign: 'center' }}>
                {loginError}
              </Typography>
            ) : null}

            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              disabled={loggingIn}
              startIcon={loggingIn ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{ fontWeight: 700 }}
            >
              {loggingIn ? 'Signing in…' : 'Login'}
            </Button>
          </Stack>
        )}

        {phase === 'link' && !linked && (
          <Stack component="form" spacing={2} onSubmit={handleLink}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, textAlign: 'center' }}>
                Link a device
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', mt: 0.5 }}>
                Enter the code shown on your TV or device.
              </Typography>
            </Box>

            <TextField
              label="Enter code"
              fullWidth
              value={code}
              onChange={(ev) => { setCode(ev.target.value.replace(/\s/g, '').toUpperCase()); if (linkError) setLinkError(''); }}
              placeholder="e.g. 4X9K2P"
              autoComplete="one-time-code"
              autoCapitalize="characters"
              disabled={linking}
              inputProps={{ style: { letterSpacing: 4, fontWeight: 700, textAlign: 'center', textTransform: 'uppercase' } }}
            />

            {linkError ? (
              <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 600, textAlign: 'center' }}>
                {linkError}
              </Typography>
            ) : null}

            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              disabled={linking || code.trim().length < 4}
              startIcon={linking ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{ fontWeight: 700 }}
            >
              {linking ? 'Linking…' : 'Link'}
            </Button>
          </Stack>
        )}

        {phase === 'link' && linked && (
          <Stack spacing={2} sx={{ alignItems: 'center', textAlign: 'center' }}>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'success.main',
                color: '#fff',
              }}
            >
              <CheckRoundedIcon sx={{ fontSize: 36 }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>Device linked</Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                Your device is now connected to this account.
              </Typography>
            </Box>
            <Button variant="contained" size="large" fullWidth onClick={linkAnother} sx={{ fontWeight: 700 }}>
              Link another device
            </Button>
          </Stack>
        )}
      </Card>

      {phase === 'link' && (
        <Stack
          direction="row"
          spacing={1.5}
          sx={{
            width: '100%',
            maxWidth: 420,
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 1.5,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            position: 'relative',
            zIndex: 1,
          }}
        >
          <Typography variant="body2" noWrap sx={{ color: 'text.secondary', minWidth: 0 }}>
            Logged in as
            {' '}
            <Box component="strong" sx={{ color: 'text.primary', fontWeight: 700 }}>{email}</Box>
          </Typography>
          <Button
            variant="outlined"
            color="inherit"
            size="small"
            onClick={handleLogout}
            startIcon={<LogoutRoundedIcon sx={{ fontSize: 16 }} />}
            sx={{ flex: '0 0 auto', fontWeight: 700, borderColor: 'divider' }}
          >
            Logout
          </Button>
        </Stack>
      )}
    </Box>
  );
};

// Force the V2 DARK theme for this page (the white wordmark needs a dark
// surface). No CssBaseline — the app shell owns global resets; we scope our
// own styles to the fixed container above.
const LinkDevicePage = () => {
  const theme = useMemo(() => buildTheme('dark'), []);
  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <LinkDeviceInner />
      </ThemeProvider>
    </StyledEngineProvider>
  );
};

export default LinkDevicePage;
